import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { Modal } from 'antd';
import { AxiosResponse } from 'axios';
import get from 'lodash/get';
import styled from 'styled-components';
import apis from 'services/apis';
import moment from 'utils/moment';
import toast from 'utils/toast';
import { buildScopeVars, SCHEDULE_ACCENT } from 'TimeOffList/theme';
import { IconX } from 'TimeOffList/components/icons';
import { verifyPinCode } from 'widgets/ModalVerifyAdminPin';
import type {
  IClosureAffectedAppointment, IShopClosure, IShopClosureSaveResult,
} from 'services/types/shopClosure';
import ClosureList from './components/ClosureList';
import ClosureForm, { ClosureFormValue } from './components/ClosureForm';
import AffectedDialog from './components/AffectedDialog';

export interface ShopDayOffRef {
  open: () => void;
  close: () => void;
}

export const shopDayOffRef = React.createRef<ShopDayOffRef>();

type Mode = { kind: 'list' } | { kind: 'form'; editing?: IShopClosure };

const errorMessage = (e: unknown, fallback: string): string => {
  const msg = get(e, 'response.data.message');
  return msg ? decodeURI(String(msg)) : fallback;
};

const ShopDayOff = forwardRef<ShopDayOffRef>((_props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const [closures, setClosures] = useState<IShopClosure[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [affected, setAffected] = useState<IClosureAffectedAppointment[]>([]);

  const today = moment().format('YYYY-MM-DD');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: AxiosResponse<{ payload: IShopClosure[] }> = await apis.getShopClosures();
      const today = moment().format('YYYY-MM-DD');
      const rows = (res?.data?.payload || []).filter(c => c.active !== false && c.endDate >= today);
      setClosures(rows.sort((a, b) => a.startDate.localeCompare(b.startDate)));
    } catch (e) {
      setClosures([]);
      toast.error(errorMessage(e, 'Failed to load shop days off'));
    } finally {
      setLoading(false);
    }
  }, []);

  const open = useCallback(() => {
    setMode({ kind: 'list' });
    setIsOpen(true);
    load();
  }, [load]);

  const close = useCallback(() => setIsOpen(false), []);

  useImperativeHandle(ref, () => ({ open, close }));

  const askPin = (title: string, cb: (pin: string) => void) => {
    verifyPinCode.current?.forceVerifyAdmin(pin => { if (pin) cb(pin); }, {
      title,
      placeholder: 'Enter Admin Pin Code',
    });
  };

  const save = (value: ClosureFormValue) => {
    const editing = mode.kind === 'form' ? mode.editing : undefined;
    askPin('SHOP DAYS OFF', async pinCode => {
      setSaving(true);
      try {
        const res: AxiosResponse<{ payload: IShopClosureSaveResult }> = await apis.saveShopClosure({
          ...value, id: editing?.id, pinCode,
        });
        const result = res?.data?.payload;
        if (!result?.closure) return; // the request layer already toasted the server message
        toast.success('Shop days off saved');
        setMode({ kind: 'list' });
        load();
        setAffected(result.affectedAppointments || []);
      } catch {
        // The request layer toasts the server message (e.g. "Invalid PIN code."); the form stays
        // open so the user can fix the input or retry the PIN.
      } finally {
        setSaving(false);
      }
    });
  };

  const remove = (c: IShopClosure) => {
    Modal.confirm({
      title: 'Delete shop days off?',
      content: `${c.startDate} to ${c.endDate}: ${c.reason}`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      centered: true,
      zIndex: 1100,
      onOk: () => new Promise<void>(resolve => {
        askPin('DELETE DAYS OFF', async pinCode => {
          try {
            await apis.deleteShopClosure({ id: c.id, pinCode });
            toast.success('Shop days off deleted');
            load();
          } catch {
            // Server message already toasted by the request layer.
          }
        });
        resolve();
      }),
    });
  };

  const editing = mode.kind === 'form' ? mode.editing : undefined;

  return (
    <>
      <ModalStyled
        open={isOpen}
        onCancel={close}
        closable={false}
        footer={null}
        centered
        destroyOnClose
        width={mode.kind === 'form' ? 920 : 720}
        styles={{
          content: { padding: 0, borderRadius: 18, overflow: 'hidden' },
          body: { padding: 0 },
          mask: { background: 'rgba(0,24,72,0.45)' },
        }}
      >
        <Root>
          <Header>
            <div className="left" />
            <span className="title">
              {mode.kind === 'list' ? 'Shop Days Off' : editing ? 'Edit Days Off' : 'Add Days Off'}
            </span>
            <div className="right">
              <CloseBtn onClick={mode.kind === 'form' ? () => setMode({ kind: 'list' }) : close} title="Close" aria-label="Close">
                <IconX size={20} strokeWidth={2.6} />
              </CloseBtn>
            </div>
          </Header>
          {mode.kind === 'list' ? (
            <ClosureList
              closures={closures}
              loading={loading}
              today={today}
              onAdd={() => setMode({ kind: 'form' })}
              onEdit={c => setMode({ kind: 'form', editing: c })}
              onDelete={remove}
            />
          ) : (
            <ClosureForm
              initial={editing ? { startDate: editing.startDate, endDate: editing.endDate, reason: editing.reason } : undefined}
              saving={saving}
              onCancel={() => setMode({ kind: 'list' })}
              onSave={save}
            />
          )}
        </Root>
      </ModalStyled>
      <AffectedDialog open={affected.length > 0} appointments={affected} onClose={() => setAffected([])} />
    </>
  );
});

ShopDayOff.displayName = 'ShopDayOff';

export default ShopDayOff;

const ModalStyled = styled(Modal)`
  .ant-modal-content { padding: 0; }
`;

const Root = styled.div`
  ${buildScopeVars(SCHEDULE_ACCENT)}
  display: flex;
  flex-direction: column;
  /* Hug the content: a one-row list stays compact, a long list or the form
     scrolls inside the capped height instead of stretching the modal. */
  max-height: min(640px, 88vh);
  overflow: hidden;
  background: var(--bg-surface);
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 16px;
  background: #E8F3FF;
  border-bottom: 1px solid #148CF1;
  flex-shrink: 0;
  .left { display: flex; align-items: center; }
  .right { display: flex; align-items: center; gap: 8px; }
  .title {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    color: #1D2129;
    font-family: Poppins, inherit;
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
    text-transform: uppercase;
    white-space: nowrap;
  }
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 34px;
  border: none;
  border-radius: 5px;
  background: #fff;
  color: #F05326;
  cursor: pointer;
  &:hover { background: #FFEFEB; }
`;
